import React from 'react';
import type { Card, CardMetric, CardRow } from '@ctd/agent-contract';
import { formatMetricValue } from '../utils/card-format';
import { hasRowCells, rowCell } from '../utils/card-rows';
import { CardFrame } from './card-frame';
import { MetricBlock, MetricFigures } from './metric-block';
import style from '../style.module.scss';

const Bars: React.FC<{ metric: CardMetric; rows: CardRow[] }> = ({
    metric,
    rows,
}) => {
    const cells = rows.map((row) => ({
        row,
        value: rowCell(row, metric.id)?.value ?? null,
    }));
    const largest = Math.max(0, ...cells.map(({ value }) => value ?? 0));

    return (
        <ul className={style.bars} aria-label={metric.label}>
            {cells.map(({ row, value }) => {
                const share =
                    value === null || largest === 0
                        ? 0
                        : Math.max(0, value) / largest;
                return (
                    <li key={row.key} className={style.bar_row}>
                        <span className={style.bar_label} title={row.label}>
                            {row.label}
                        </span>
                        {value === null ? (
                            <span className={style.bar_track} />
                        ) : (
                            <span
                                className={style.bar_track}
                                role="meter"
                                aria-label={row.label}
                                aria-valuemin={0}
                                aria-valuemax={largest || 1}
                                aria-valuenow={value}
                                aria-valuetext={formatMetricValue({
                                    unit: metric.unit,
                                    value,
                                })}
                            >
                                <span
                                    className={style.bar_fill}
                                    style={{
                                        width: `${Number(
                                            (share * 100).toFixed(2)
                                        )}%`,
                                    }}
                                />
                            </span>
                        )}
                        <span className={style.bar_value}>
                            {formatMetricValue({ unit: metric.unit, value })}
                        </span>
                    </li>
                );
            })}
        </ul>
    );
};

export const BreakdownCard: React.FC<{
    card: Extract<Card, { shape: 'breakdown' }>;
}> = ({ card }) => (
    <CardFrame card={card}>
        <div className={style.metrics}>
            {card.primary.map((metric) => (
                <MetricBlock key={metric.id} metric={metric} title={card.title}>
                    <MetricFigures metric={metric} />
                    {hasRowCells(card.rows, metric) && (
                        <Bars metric={metric} rows={card.rows} />
                    )}
                </MetricBlock>
            ))}
        </div>
    </CardFrame>
);
